import React from "react";
import { ViewProps } from "react-native";
import { View, createElement } from "react-native-web";
import {moderateScale} from "../../tools/Scaling";
//import { XmlProps } from "./xml";
/*

  ColumnMajorTransformMatrix

  [a, b, c, d, tx, ty]

  This matrix can be visualized as:

  ╔═      ═╗
  ║ a c tx ║
  ║ b d ty ║
  ║ 0 0 1  ║
  ╚═      ═╝

*/
export type ColumnMajorTransformMatrix = [
    number,
    number,
    number,
    number,
    number,
    number
    ];
// rgba values inside range 0 to 1 inclusive
// rgbaArray = [r, g, b, a]
export type rgbaArray = ReadonlyArray<number>;

// argb values inside range 0x00 to 0xff inclusive
// int32ARGBColor = 0xaarrggbb
export type int32ARGBColor = number;
type NumberProp = string | number;
type FillRule = "evenodd" | "nonzero";
type Linecap = "butt" | "square" | "round";
type Linejoin = "miter" | "bevel" | "round";
type Color = int32ARGBColor | rgbaArray | string;
type NumberArray = NumberProp[] | NumberProp;
interface TransformObject {
    translate?: NumberArray;
    translateX?: NumberProp;
    translateY?: NumberProp;
    origin?: NumberArray;
    originX?: NumberProp;
    originY?: NumberProp;
    scale?: NumberArray;
    scaleX?: NumberProp;
    scaleY?: NumberProp;
    skew?: NumberArray;
    skewX?: NumberProp;
    skewY?: NumberProp;
    rotation?: NumberProp;
    x?: NumberArray;
    y?: NumberArray;
}
// export type SvgXmlProps =
//     ViewProps & {
//     height: NumberProp;
//     width: NumberProp;
//     viewBox?: string;
//     preserveAspectRatio?: string;
//     color?: Color;
//     title?: string;
//     opacity?: NumberProp;
//     fill?: Color;
//     fillOpacity?: number;
//     fillRule?: FillRule;
//     stroke?: Color;
//     strokeWidth?: NumberProp;
//     strokeOpacity?: NumberProp;
//     strokeDasharray?: ReadonlyArray<NumberProp> | NumberProp;
//     strokeDashoffset?: NumberProp;
//     strokeLinecap?: Linecap;
//     strokeLinejoin?: Linejoin;
//     strokeMiterlimit?: NumberProp;
//     clipRule?: FillRule;
//     clipPath?: string;
//     transform?: ColumnMajorTransformMatrix | string | TransformObject;
//     translate?: NumberArray;
//     translateX?: NumberProp;
//     translateY?: NumberProp;
//     origin?: NumberArray;
//     originX?: NumberProp;
//     originY?: NumberProp;
//     scale?: NumberArray;
//     scaleX?: NumberProp;
//     scaleY?: NumberProp;
//     skew?: NumberArray;
//     skewX?: NumberProp;
//     skewY?: NumberProp;
//     rotation?: NumberProp;
//     x?: NumberArray;
//     y?: NumberArray;
//     vectorEffect?:
//         | "none"
//         | "non-scaling-stroke"
//         | "nonScalingStroke"
//         | "default"
//         | "inherit"
//         | "uri";
//     pointerEvents?: "box-none" | "none" | "box-only" | "auto";
//     id?: string;
//     marker?: string;
//     markerStart?: string;
//     markerMid?: string;
//     markerEnd?: string;
//     mask?: string;
// };
const SvgXml = React.forwardRef(
    ({ xml, ...props }, fowardRef) => {
        const { attributes, innerSVG } = parseSVG(xml);
        const camelAttributes = kebabToCamel(attributes);
        const svgRef = React.createRef();
        React.useLayoutEffect(() => {
            if (!svgRef.current) {
                return;
            }
            svgRef.current.innerHTML = innerSVG;
        }, [innerSVG, svgRef]);

        const {
            height,
            width,
            viewBox,
            preserveAspectRatio,
            color,
            title,
            opacity,
            fill,
            fillOpacity,
            fillRule,
            transform,
            stroke,
            strokeWidth,
            strokeOpacity,
            strokeDasharray,
            strokeDashoffset,
            strokeLinecap,
            strokeLinejoin,
            strokeMiterlimit,
            clipRule,
            clipPath,
            vectorEffect,
            pointerEvents,
            id,
            markerStart,
            markerMid,
            markerEnd,
            mask,
            originX,
            originY,
            translate,
            scale,
            rotation,
            skewX,
            skewY,
            style,
            // props that should be applyed to the View container
            ...containerProps
        } = props;

        const transformArr = [];

        if (originX != null || originY != null) {
            transformArr.push(`translate(${originX || 0}, ${originY || 0})`);
        }
        if (translate != null) {
            transformArr.push(`translate(${translate})`);
        }
        if (scale != null) {
            transformArr.push(`scale(${scale})`);
        }
        // rotation maps to rotate, not to collide with the text rotate attribute (which acts per glyph rather than block)
        if (rotation != null) {
            transformArr.push(`rotate(${rotation})`);
        }
        if (skewX != null) {
            transformArr.push(`skewX(${skewX})`);
        }
        if (skewY != null) {
            transformArr.push(`skewY(${skewY})`);
        }
        if (originX != null || originY != null) {
            transformArr.push(`translate(${-originX || 0}, ${-originY || 0})`);
        }

        // these props should override the xml props
        const overrideProps = {
            transform: transformArr.length ? transformArr.join(" ") : transform,
            //style: { width: "100%", height: "100%" },
            style: {},
            viewBox,
            preserveAspectRatio,
            color,
            title,
            opacity,
            fill,
            fillOpacity,
            fillRule,
            stroke,
            strokeWidth,
            strokeOpacity,
            strokeDasharray,
            strokeDashoffset,
            strokeLinecap,
            strokeLinejoin,
            strokeMiterlimit,
            clipRule,
            clipPath,
            vectorEffect,
            pointerEvents,
            id,
            markerStart,
            markerMid,
            markerEnd,
            mask
        };

        const finalProps = {
            ...camelAttributes,
            ...removeUndefined(overrideProps)
        };
        const Svg = createElement("svg", { ref: svgRef, ...finalProps });

        const containerDefaultStyles = {
            width,
            height
        };
        return (
            <View
                ref={fowardRef}
                {...containerProps}
                style={{   width: "100%",
                    height: "auto"}}
            >
                {Svg}
            </View>
        );
    }
);

SvgXml.displayName = "Svg";

export default SvgXml;

/** polyfill for Node < 12 */
function matchAll(str) {
    return re => {
        const matches = [];
        let groups;
        while ((groups = re.exec(str))) {
            matches.push(groups);
        }
        return matches;
    };
}

function parseSVG(svg: string) {
    const content = svg.match(/<svg(.*)<\/svg>/ims)[1];
    const [, attrs, innerSVG] = content.match(/(.*?)>(.*)/ims);
    const attributes = [
        ...matchAll(attrs)(/([a-z0-9]+)(=['"](.*?)['"])?/gims)
    ].map(([, key, , value]) => ({ [key]: value }));
    return { attributes, innerSVG };
}

function kebabToCamel(attrs) {
    const camelObj = {};
    attrs.forEach(attr => {
        const key = Object.keys(attr)[0];
        camelObj[key.replace(/-./g, x => x.toUpperCase()[1])] = attr[key];
    });
    return camelObj;
}

function removeUndefined(obj) {
    const finalObj = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] !== undefined) finalObj[key] = obj[key];
    });
    return finalObj;
}
